// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { FormattedMessage, defineMessages, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import Loading from '../../../../elements/Loading/Loading';
import Modal from '../../../../elements/Modal/Modal';
import FlightBox from '../FlightBox/FlightBox';
import AlertContainer from '../../../alert/container/AlertContainer';
import 'moment/locale/es';
import 'moment/locale/en-gb';
import EditFlightContainer from '../../../edit-flight/containers/EditFlightContainer';
import { animateScroll as scroll } from 'react-scroll/modules';

import style from './FlightsCurrent.scss';
import commonStyle from '../FlightsViewCommon.scss';
import icons from '../../../../assets/styles/icons.scss';
import modalStyle from '../../../../assets/styles/modal.scss';
import buttonStyle from '../../../../assets/styles/button.scss';

import type { Flight } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { LocalValuesState } from '../../../local-values/duck';

type Props = {
  +fetchActiveFlights: () => void;
  +resetFlightsState: () => void;
  +isFetchingActive?: boolean;
  +activeFlights?: Flight[];
  +fetchActiveError?: HTTPError;
  +deleteFlight: (flightId: string) => void;
  +isDeleting: boolean;
  +deleteActiveError?: HTTPError;
  +successDelete?: boolean;
  +locale: string;
  +resetAlertState: () => void;
  +localValues: LocalValuesState;
  +fetchError?: HTTPError;
  +putError?: HTTPError;
  +successUpdate?: boolean;
};

type State = {
  modalVisible: boolean;
  editModalVisible: boolean;
  modalIdFlight: string;
};

const messages = defineMessages({
  title: {
    id: 'flights.active.document.title',
  },
  description: {
    id: 'flights.active.document.description',
  },
});

export default class FlightsCurrent extends Component<Props, State> {
  state = {
    modalVisible: false,
    editModalVisible: false,
    modalIdFlight: '',
  };

  // Only in browser
  componentDidMount() {
    const { fetchActiveFlights, activeFlights } = this.props;
    if (!activeFlights) {
      fetchActiveFlights();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { modalVisible, editModalVisible } = this.state;
    const { successDelete, fetchError, putError, successUpdate } = prevProps;
    if (!successDelete && this.props.successDelete && modalVisible) {
      this.setState({
        modalVisible: false,
      });
    }
    if ((!fetchError && this.props.fetchError) ||
      (!putError && this.props.putError) && editModalVisible) {
      this.setState({
        editModalVisible: false,
      });
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
    if ((!successUpdate && this.props.successUpdate) && editModalVisible) {
      this.setState({
        editModalVisible: false,
      });
      this.props.fetchActiveFlights();
    }
  }

  componentWillUnmount() {
    const { resetFlightsState, resetAlertState } = this.props;
    resetFlightsState();
    resetAlertState();
  }

  handleOnClose() {
    const { isDeleting } = this.props;
    if (!isDeleting) {
      this.setState({
        modalVisible: false,
      });
    }
  }

  handleCloseEditModal = () => {
    this.setState({
      editModalVisible: false,
    });
  };

  handleClickDelete(e: Event, modalIdFlight: string) {
    e.preventDefault();
    this.setState({
      modalVisible: true,
      modalIdFlight,
    });
  }

  handleOnDelete(modalIdFlight: string) {
    const { isDeleting, deleteFlight } = this.props;
    if (!isDeleting) {
      deleteFlight(modalIdFlight);
    }
  }

  handleClickEdit(e: Event, modalIdFlight: string) {
    e.preventDefault();
    this.setState({
      editModalVisible: true,
      modalIdFlight,
    });
  }

  render() {
    const { activeFlights, isFetchingActive, isDeleting, fetchActiveError, localValues } = this.props;
    const { modalVisible, modalIdFlight, editModalVisible } = this.state;
    const noResult = (!activeFlights || !activeFlights.length) && !isFetchingActive;
    const totalOrderCount = !activeFlights ? 0 :
      activeFlights.reduce((acum: number, flight: Flight) => acum + flight.orderCount, 0);
    const flights = activeFlights && activeFlights.map((flight: Flight, n: number) => {
      return <FlightBox key={n}
                        flight={flight}
                        localValues={localValues}
                        handleClickEdit={this.handleClickEdit.bind(this)}
                        handleClickDelete={this.handleClickDelete.bind(this)}/>
    });

    const productsQuantity = 0;
    return (
      <Fragment>
        <Helmet key="helmet">
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <section key="mainWrap" className={commonStyle.mainWrap}>
          <section className={commonStyle.rightWrap}>
            <AlertContainer showId={['flights', 'edit-flight']}/>
            <header>
              <FormattedMessage id="flights.active.title">{
                txt => <p className={commonStyle.title}><i className={icons.iconSuitcaseMedium}/>{txt}</p>
              }</FormattedMessage>
              {fetchActiveError && Math.round(fetchActiveError.code / 100) === 5 ?
                <FormattedMessage id="httpErrors.fetch.500">{
                  txt => <p>{txt}</p>
                }</FormattedMessage> :
                <FormattedMessage id={noResult ? 'flights.active.noResults' : 'flights.active.subtitle'}>{
                  txt => <p className={commonStyle.subtitle}>{txt}{!noResult && `: ${totalOrderCount}`}</p>
                }</FormattedMessage>}
            </header>
            <section className={commonStyle.flightsWrap}>
              {flights}
            </section>
            {isFetchingActive && <Loading className={commonStyle.loading} color="orange"/>}
          </section>
        </section>
        <Modal key="modal"
               open={modalVisible}
               height={180}
               handleClose={this.handleOnClose.bind(this)}>
          {isDeleting && <Loading type="sk-three-bounce"
                                  className={style.modalLoading}
                                  number={3}/>}
          {!isDeleting && [
            <i key="icon" className={classNames(icons.symbolExclamationCircle, modalStyle.title)}/>,
            <FormattedMessage id="flights.active.modal.text" key="content">{
              txt => <p className={modalStyle.content}>{txt}</p>
            }</FormattedMessage>,
            <section className={modalStyle.buttonsWrap} key="wrap">
              <FormattedMessage id="flights.active.modal.button.cancel">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey, style.next)}
                               onClick={this.handleOnClose.bind(this)}
                >{txt}</button>
              }</FormattedMessage>
              <FormattedMessage id="flights.active.modal.button.delete">{
                txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.next)}
                               onClick={() => this.handleOnDelete(modalIdFlight)}
                >{txt}</button>
              }</FormattedMessage>
            </section>,
          ]}
        </Modal>
        {editModalVisible && <EditFlightContainer isOpen
                                                  id={modalIdFlight}
                                                  handleCloseModal={this.handleCloseEditModal}/>}
      </Fragment>);
  }
}


FlightsCurrent.propTypes = {
  fetchActiveFlights: PropTypes.func.isRequired,
  resetFlightsState: PropTypes.func.isRequired,
  isFetchingActive: PropTypes.bool,
  activeFlights: PropTypes.array,
  fetchActiveError: PropTypes.object,
  deleteFlight: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  deleteActiveError: PropTypes.bool,
  successDelete: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  localValues: PropTypes.object.isRequired,
  fetchError: PropTypes.bool,
  putError: PropTypes.object,
  successUpdate: PropTypes.bool,
};

FlightsCurrent.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/flights/components/FlightCurrent/FlightsCurrent.jsx